import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Fade,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { getConnectorInfo } from './utils/socketTypes';
import { normalizeNetworkName } from '../../common/components/Map/utils/filterUtils';
import { socketTypeList } from '../../common/components/Map/utils/Display';

const FilterGroup = ({ title, options, selectedValues, onToggle, renderOption }) => {
  const [expanded, setExpanded] = useState(true);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accordionSurface = isDark ? alpha(theme.palette.common.white, 0.03) : 'transparent';
  const accordionBorder = isDark ? alpha(theme.palette.common.white, 0.08) : 'rgba(0, 0, 0, 0.08)';

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        boxShadow: 'none',
        backgroundColor: accordionSurface,
        borderRadius: 2,
        border: `1px solid ${accordionBorder}`,
        '&:before': { display: 'none' }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: isDark ? alpha(theme.palette.common.white, 0.7) : theme.palette.text.secondary }} />}
        sx={{ color: theme.palette.text.primary }}
      >
        <Typography fontWeight="bold" color="text.primary">{title}</Typography>
        {selectedValues.length > 0 && (
          <Chip
            label={selectedValues.length}
            size="small"
            color="primary"
            sx={{ ml: 1 }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: accordionSurface }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: options.length > 8 ? 'repeat(auto-fill, minmax(100px, 1fr))' : 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 2,
          }}
        >
          {options.map((option) =>
            renderOption ? renderOption(option) : (
              <FilterOption
                key={option.id}
                option={option}
                isSelected={selectedValues.includes(option.id)}
                onToggle={() => onToggle(option.id)}
              />
            )
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const FilterOption = ({ option, isSelected, onToggle }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const baseBg = isDark ? alpha(theme.palette.common.white, 0.05) : theme.palette.grey[100];
  const hoverBg = isDark ? alpha(theme.palette.common.white, 0.1) : theme.palette.grey[200];
  const selectedBg = isDark ? alpha(theme.palette.primary.main, 0.22) : alpha(theme.palette.primary.main, 0.1);
  const selectedBorder = isDark ? alpha(theme.palette.primary.main, 0.85) : theme.palette.primary.main;
  const defaultBorder = isDark ? alpha(theme.palette.common.white, 0.08) : 'transparent';

  return (
    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
      <Paper
        onClick={onToggle}
        sx={{
          p: 2,
          cursor: 'pointer',
          border: 2,
          borderColor: isSelected ? selectedBorder : defaultBorder,
          backgroundColor: isSelected ? selectedBg : baseBg,
          transition: 'all 0.2s',
          position: 'relative',
          backdropFilter: isDark ? 'blur(8px)' : 'none',
          '&:hover': {
            borderColor: isSelected ? selectedBorder : alpha(theme.palette.primary.light, 0.6),
            backgroundColor: isSelected ? selectedBg : hoverBg,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {option.icon && (
            <Box
              sx={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? alpha(theme.palette.common.white, 0.08) : theme.palette.common.white,
                borderRadius: '50%',
                border: isSelected ? 2 : 1,
                borderColor: isSelected ? selectedBorder : (isDark ? alpha(theme.palette.common.white, 0.12) : theme.palette.grey[300]),
              }}
            >
              <Box
                component="img"
                src={option.icon}
                alt={option.label}
                sx={{ 
                  width: 32, 
                  height: 32,
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          )}
          <Typography
            variant="caption"
            fontWeight={isSelected ? 'bold' : 'medium'}
            color={isSelected ? theme.palette.primary.main : theme.palette.text.secondary}
            textAlign="center"
          >
            {option.label}
          </Typography>
          {option.count !== undefined && (
            <Chip
              label={option.count}
              size="small"
              color={isSelected ? 'primary' : 'default'}
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>
        {isSelected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 24,
              height: 24,
              backgroundColor: theme.palette.success.main,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isDark ? '0 4px 10px rgba(0,0,0,0.6)' : '0 4px 6px rgba(0,0,0,0.2)'
            }}
          >
            <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
          </motion.div>
        )}
      </Paper>
    </motion.div>
  );
};

const FilterPanel = ({ onClose, filters, setFilters, chargers = [] }) => {
  const panelRef = useRef(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelSurface = isDark ? alpha(theme.palette.background.paper, 0.96) : '#ffffff';
  const panelBorder = isDark ? `1px solid ${alpha(theme.palette.common.white, 0.08)}` : '1px solid rgba(0, 0, 0, 0.08)';
  const headerBg = isDark ? alpha(theme.palette.common.white, 0.05) : alpha(theme.palette.primary.main, 0.08);
  const headerBorder = isDark ? alpha(theme.palette.common.white, 0.08) : theme.palette.divider;
  const bodyBg = isDark ? alpha(theme.palette.common.white, 0.02) : 'transparent';
  const footerBg = isDark ? alpha(theme.palette.common.white, 0.03) : theme.palette.grey[50];
  const footerBorder = isDark ? alpha(theme.palette.common.white, 0.08) : theme.palette.divider;

  // Process charger data to extract dynamic filter options
  const { socketTypes, speeds, networks } = useMemo(() => {
    try {
      if (!chargers || chargers.length === 0) {
        return { socketTypes: [], speeds: [], networks: [] };
      }

    const socketTypeCounts = {};
    const speedCounts = {};
    const networkCounts = {};

    chargers.forEach(charger => {
      // Process socket types/connectors
      if (charger.connectors && Array.isArray(charger.connectors)) {
        charger.connectors.forEach(conn => {
          try {
            const connectorType = conn.connectorType || conn.connectorName || conn.type;
            if (connectorType && typeof connectorType === 'string') {
              const normalizedType = connectorType.toLowerCase().trim();
              if (normalizedType) {
                socketTypeCounts[normalizedType] = (socketTypeCounts[normalizedType] || 0) + 1;
              }
            }
          } catch (error) {
          }
        });
      }

      // Process charging speeds (based on power)
      if (charger.connectors && Array.isArray(charger.connectors)) {
        charger.connectors.forEach(conn => {
          try {
            const power = parseInt(conn.power || conn.powerRating || 0) || 0;
            let speedCategory = 'slow';
            if (power >= 50) speedCategory = 'fast';
            else if (power >= 22) speedCategory = 'medium';
            speedCounts[speedCategory] = (speedCounts[speedCategory] || 0) + 1;
          } catch (error) {
            // Default to slow speed
            speedCounts['slow'] = (speedCounts['slow'] || 0) + 1;
          }
        });
      }

      // Process networks
      try {
        const possibleNetworkFields = [
          charger?.name,
          charger?.chargepoint_name,
          charger?.operator,
          charger?.provider,
          charger?.owner,
          charger?.network,
          charger?.team_name,
        ].filter(Boolean);

        let detectedNetwork = 'generic';
        for (const networkField of possibleNetworkFields) {
          try {
            const normalizedNetwork = normalizeNetworkName(networkField);
            if (normalizedNetwork && normalizedNetwork !== 'other' && normalizedNetwork !== 'generic') {
              detectedNetwork = normalizedNetwork;
              break;
            }
          } catch (error) {
            continue;
          }
        }
        networkCounts[detectedNetwork] = (networkCounts[detectedNetwork] || 0) + 1;
      } catch (error) {
        networkCounts['generic'] = (networkCounts['generic'] || 0) + 1;
      }
    });

    // Convert to filter options format
    const socketTypeOptions = Object.entries(socketTypeCounts).map(([type, count]) => {
      // Ensure type is a string
      const normalizedType = String(type).toLowerCase().trim();
      
      const connectorInfo = socketTypeList.find(s => {
        try {
          const sValue = String(s.value || '').toLowerCase();
          const sId = String(s.id || '').toLowerCase();
          const sType = String(s.type || '').toLowerCase();
          const sLabel = String(s.label || '').toLowerCase();
          
          // Also check if the normalized type matches any part of the connector type
          const typeMatch = sValue === normalizedType || sId === normalizedType || sType === normalizedType || sLabel === normalizedType;
          const partialMatch = sValue.includes(normalizedType) || sType.includes(normalizedType) || normalizedType.includes(sValue);
          
          return typeMatch || partialMatch;
        } catch (error) {
          return false;
        }
      });
      
      
      return {
        id: normalizedType,
        label: connectorInfo?.type || normalizedType.toUpperCase(),
        count,
        icon: connectorInfo?.src || connectorInfo?.icon || '/assets/connectorSvg/img_connector_1.svg'
      };
    }).filter(option => option.id && option.count > 0);

    const speedOptions = [
      { 
        id: 'fast', 
        label: 'Fast (>50kW)', 
        count: speedCounts.fast || 0, 
        icon: '/assets/speed/fast.png' 
      },
      { 
        id: 'medium', 
        label: 'Medium (22-50kW)', 
        count: speedCounts.medium || 0, 
        icon: '/assets/speed/medium.png' 
      },
      { 
        id: 'slow', 
        label: 'Slow (<22kW)', 
        count: speedCounts.slow || 0, 
        icon: '/assets/speed/slow.png' 
      }
    ].filter(option => option.count > 0);

    const iconMapping = {
      'iocl': 'IOCL',
      'hp': 'HP',
      'bpcl': 'BPCL',
      'tata': 'Tata',
      'ather': 'Ather',
      'jio_bp': 'JioBP',
      'volttic': 'Volttic',
      'zynka': 'Zynka',
      'generic': 'Generic'
    };

    const networkOptions = Object.entries(networkCounts).map(([network, count]) => {
      const iconPath = network === 'generic' 
        ? '/assets/networkFilter/Generic.svg'
        : `/assets/networkFilter/${iconMapping[network] || 'Generic'}.svg`;
      
      
      return {
        id: network,
        label: network === 'generic' ? 'Other Chargers' : network.toUpperCase(),
        count,
        icon: iconPath
      };
    }).sort((a, b) => b.count - a.count);


      return {
        socketTypes: socketTypeOptions,
        speeds: speedOptions,
        networks: networkOptions
      };
    } catch (error) {
      return { socketTypes: [], speeds: [], networks: [] };
    }
  }, [chargers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const toggleFilter = (category, value) => {
    setFilters((prev) => {
      const currentFilters = prev[category] || [];
      const newFilters = currentFilters.includes(value)
        ? currentFilters.filter((v) => v !== value)
        : [...currentFilters, value];

      return {
        ...prev,
        [category]: newFilters,
      };
    });
  };

  const clearAllFilters = () => {
    setFilters({
      socketTypes: [],
      speeds: [],
      networks: [],
    });
  };

  const activeFilterCount = Object.values(filters).flat().length;

  return (
    <AnimatePresence>
      <Fade in={true}>
        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, x: 20, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'absolute',
            top: 80,
            right: 20,
            zIndex: 1100,
            width: '90vw',
            maxWidth: 560,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              overflow: 'hidden',
              backgroundColor: panelSurface,
              border: panelBorder,
              backdropFilter: isDark ? 'blur(14px)' : 'none',
              boxShadow: isDark ? '0 30px 60px rgba(0,0,0,0.55)' : '0 20px 35px rgba(0,0,0,0.12)'
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${headerBorder}`,
                backgroundColor: headerBg,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                  Filters
                </Typography>
                {activeFilterCount > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: isDark ? alpha(theme.palette.common.white, 0.7) : theme.palette.text.secondary }}
                  >
                    {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={clearAllFilters} disabled={activeFilterCount === 0}>
                  Clear All
                </Button>
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Filter Groups */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2, backgroundColor: bodyBg }}>
              {socketTypes.length > 0 && (
                <FilterGroup
                  title="Socket Types"
                  options={socketTypes}
                  selectedValues={filters.socketTypes || []}
                  onToggle={(value) => toggleFilter('socketTypes', value)}
                  renderOption={(option) => {
                    const connectorInfo = getConnectorInfo(option.id);
                    return (
                      <FilterOption
                        key={option.id}
                        option={{ ...option, icon: connectorInfo.icon }}
                        isSelected={(filters.socketTypes || []).includes(option.id)}
                        onToggle={() => toggleFilter('socketTypes', option.id)}
                      />
                    );
                  }}
                />
              )}

              {speeds.length > 0 && (
                <FilterGroup
                  title="Charging Speed"
                  options={speeds}
                  selectedValues={filters.speeds || []}
                  onToggle={(value) => toggleFilter('speeds', value)}
                />
              )}

              {networks.length > 0 && (
                <FilterGroup
                  title="Networks"
                  options={networks}
                  selectedValues={filters.networks || []}
                  onToggle={(value) => toggleFilter('networks', value)}
                />
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${footerBorder}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: footerBg,
              }}
            >
              <Badge badgeContent={activeFilterCount} color="primary">
                <Typography variant="body2" color="text.secondary">
                  Active Filters
                </Typography>
              </Badge>
              <Button variant="contained" onClick={onClose}>
                Apply Filters
              </Button>
            </Box>
          </Paper>
        </motion.div>
      </Fade>
    </AnimatePresence>
  );
};

export default FilterPanel;
